<script lang="ts">
  import { resolveBuilding } from '$lib/catalogue'
  import type { CityListEntry } from './stores'
  import { relativeTime, lastEditedLabel } from './format'
  import { renderThumbnail } from './thumbnail'

  interface Props {
    entry: CityListEntry
    onOpen: () => void
    onRename: () => void
    onDelete: () => void
    onExport: () => void
  }

  const { entry, onOpen, onRename, onDelete, onExport }: Props = $props()

  /** SVG thumbnail markup. Recomputes only when the plan changes (cheap —
   *  sub-millisecond for a few hundred buildings). The {@html} below trusts
   *  this string; no external input lands here, so XSS isn't a concern. */
  const thumbnailSvg = $derived(
    renderThumbnail(entry.plan, (id) => resolveBuilding(id, entry.importedCity ?? undefined)),
  )

  let menuOpen = $state(false)

  function toggleMenu(ev: MouseEvent) {
    ev.stopPropagation()
    menuOpen = !menuOpen
  }
  function close() {
    menuOpen = false
  }
  function pick(action: () => void) {
    return (ev: MouseEvent) => {
      ev.stopPropagation()
      menuOpen = false
      action()
    }
  }

  function onCardClick(ev: MouseEvent) {
    if (menuOpen) {
      close()
      return
    }
    if ((ev.target as HTMLElement).closest('[data-no-open]')) return
    onOpen()
  }
</script>

<div
  class="group relative flex cursor-pointer flex-col rounded-xl border border-border-rule bg-surface-card p-5 shadow-md transition hover:border-accent"
  onclick={onCardClick}
  onkeydown={(e) => {
    if (e.key === 'Enter') onOpen()
  }}
  role="button"
  tabindex="0"
>
  <div
    class="-mx-5 -mt-5 mb-3 flex items-center justify-center rounded-t-xl border-b border-border-rule bg-bg-base/40 p-2"
    aria-hidden="true"
  >
    <div class="thumbnail h-32 w-full">
      <!-- SVG markup is produced by our own renderThumbnail() pure function
           from PlanBuilding records and the resolved catalogue. No user-
           supplied or imported text reaches this output, so XSS isn't a
           concern. The {@html} sink is needed because rendering each rect
           via Svelte's template would cost more than the SVG string. -->
      <!-- eslint-disable-next-line svelte/no-at-html-tags -->
      {@html thumbnailSvg}
    </div>
  </div>

  <header class="flex items-start justify-between gap-3">
    <h3 class="text-lg font-semibold text-text-primary">{entry.plan.name}</h3>
    <div class="relative" data-no-open>
      <button
        type="button"
        class="rounded p-1 text-text-secondary hover:bg-surface-card-recessed hover:text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-bright"
        aria-label="More actions"
        onclick={toggleMenu}>…</button
      >
      {#if menuOpen}
        <!-- Popover sits on a chrome surface (`bg-surface-4`, dark on every
             theme by design). Text must use `chrome-text` not `text-primary`
             — the latter is the content-card text colour, which goes dark on
             light/light-blue themes and unreadable on dark chrome. The
             chrome-text variable is light on every theme, designed exactly
             for this surface. -->
        <div
          class="absolute right-0 top-full z-10 mt-1 w-44 rounded-md border border-border-rule bg-surface-4 p-1 shadow-xl"
          role="menu"
        >
          <button
            type="button"
            class="block w-full rounded px-3 py-2 text-left text-sm text-chrome-text hover:bg-surface-3"
            onclick={pick(onRename)}>Rename</button
          >
          <button
            type="button"
            class="block w-full rounded px-3 py-2 text-left text-sm text-chrome-text hover:bg-surface-3"
            onclick={pick(onExport)}>Export city</button
          >
          <button
            type="button"
            class="block w-full rounded px-3 py-2 text-left text-sm text-status-neg hover:bg-status-neg/10"
            onclick={pick(onDelete)}>Delete</button
          >
        </div>
      {/if}
    </div>
  </header>

  <dl class="mt-3 grid grid-cols-2 gap-2 text-xs text-text-secondary">
    <div>
      <dt class="text-text-muted">Imported</dt>
      <dd>
        {#if entry.importedCity}
          {relativeTime(entry.importedCity.importedAt)}
        {:else}
          —
        {/if}
      </dd>
    </div>
    <div>
      <dt class="text-text-muted">Edited</dt>
      <dd>{lastEditedLabel(entry.plan.createdAt, entry.plan.updatedAt)}</dd>
    </div>
  </dl>

  <ul class="mt-4 flex flex-wrap gap-2 text-xs">
    <li class="rounded bg-surface-card-recessed px-2 py-1 text-text-secondary">
      {entry.buildingCount} buildings
    </li>
    <li class="rounded bg-surface-card-recessed px-2 py-1 text-text-secondary">
      {entry.greatBuildingCount} GBs
    </li>
  </ul>
</div>

<style>
  /* Nested SVG fills the thumbnail box. Width/height attributes are still
     emitted by renderThumbnail() for non-CSS contexts (tests, future
     standalone use); the !important ensures the container wins. */
  .thumbnail :global(svg) {
    width: 100%;
    height: 100%;
    display: block;
  }
</style>
